import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../store/useAuth";
import useAppointmentStore from "../store/useAppointmentStore";
import { isFramed } from "../utils/embed";
import { hasActiveFacilityScope } from "../utils/facilityScope";
import EmbedNeedsSlug from "../components/EmbedNeedsSlug";

// FacilityEntry routes that resolve a slug (path or query).
const isFacilityEntryPath = (path: string): boolean =>
  path === "/hospital" || path.startsWith("/hospital/");

const isQRRedirectPath = (path: string): boolean => {
  return path.includes("qr-redirect") && path.includes("token=");
};

const extractFacilityId = (path: string): number | null => {
  try {
    if (path.includes("token=")) {
      const searchParams = new URLSearchParams(path.split("?")[1]);
      const token = searchParams.get("token");
      if (token) {
        const decodedFacilityId = atob(decodeURIComponent(token));
        return parseInt(decodedFacilityId, 10);
      }
    }
    return null;
  } catch (error) {
    console.error("Error extracting facilityId:", error);
    return null;
  }
};

// Utility function to detect and process specialization URLs
const isSpecializationPath = (path: string): boolean => {
  return path.includes("/specializations/") && path.split("/").length >= 4;
};

// Function to extract and save specialization info from path
const processSpecializationPath = (path: string) => {
  
  if (!isSpecializationPath(path)) return;
  
  // Store path in multiple locations
  localStorage.setItem("intendedPath", path);
  sessionStorage.setItem("originalIntendedPath", path);
  sessionStorage.setItem("storedSpecializationPath", path);
  
  // Extract IDs
  const pathParts = path.split('/');
  if (pathParts.length >= 4) {
    const specId = pathParts[2];
    sessionStorage.setItem("qrSpecializationId", specId);
    localStorage.setItem("fromSpecializationQR", "true");
    localStorage.setItem("specializationSelected", "true");
    
    if (pathParts.length >= 6) {
      const facId = pathParts[4];
      sessionStorage.setItem("qrFacilityId", facId);
      localStorage.setItem("selectedFacilityId", facId);
      localStorage.setItem("hospitalSelected", "true");
    }
  }
};

const PUBLIC_ROUTES = [
  "/auth",
  "/verify-otp",
  "/forgot-password-otp",
  "/forgot-password-reset",
  "/get-user",
  "/hospital",
  "/hospitals"
];

const isPublicRoute = (pathname: string): boolean => {
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route));
};

interface RouteHandlerProps {
  children: React.ReactNode;
}

export const RouteHandler = ({ children }: RouteHandlerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isRegistred } = useAuth();
  const { setSelectedFacility } = useAppointmentStore();
  
  // Add refs to track navigation state and prevent rapid redirects
  const isNavigatingRef = useRef(false);
  const lastNavigationTimeRef = useRef(0);
  const lastPathRef = useRef(location.pathname);
  const isOnGetUserPage = useRef(false);
  // Handle QR code parsing first
  useEffect(() => {
    // Don't process during logout
    if (sessionStorage.getItem("isLoggingOut") === "true") {
      return;
    }
    
    const fullPath = location.pathname + location.search;
    
    // Handle regular QR redirect paths
    if (isQRRedirectPath(fullPath)) {
      const facilityId = extractFacilityId(fullPath);
      if (facilityId) {
        setSelectedFacility(facilityId);
        localStorage.setItem("selectedFacilityId", facilityId.toString());
        localStorage.setItem("hospitalSelected", "true");
        localStorage.setItem("fromQR", "true");
      }
    }
      // Handle specialization paths - CRITICAL NEW CODE
    if (isSpecializationPath(location.pathname)) {
      processSpecializationPath(location.pathname);
    }
  }, [location.pathname, location.search, setSelectedFacility]);

  useEffect(() => {    // Skip all routing logic during logout process
    if (sessionStorage.getItem("isLoggingOut") === "true") {
      return;
    }
    
    // CRITICAL FIX: Skip routing logic immediately after signup completion
    if (localStorage.getItem("signupCompleted") === "true") {
      localStorage.removeItem("signupCompleted");
      return;
    }
      // Update our reference to know if we're on get-user page
    isOnGetUserPage.current = location.pathname === "/get-user";
    
    // CRITICAL FIX: If current path is a specialization path, store it first
    if (isSpecializationPath(location.pathname)) {
      processSpecializationPath(location.pathname);
    }
    
    // Debounce navigation - prevent multiple redirects within short time
    if (isNavigatingRef.current) {
      return; // Don't start another navigation while one is in progress
    }
    
    // Prevent navigation if path hasn't changed significantly
    if (lastPathRef.current === location.pathname) {
      return;
    }
    
    // Enforce a minimum time between navigations (500ms)
    const now = Date.now();
    if (now - lastNavigationTimeRef.current < 500) {
      return;
    }

    // Check for special flows that should not be interrupted
    const isSpecialFlow = 
      // Forgot password flow
      location.pathname === "/forgot-password-otp" || 
      location.pathname === "/forgot-password-reset" ||
      localStorage.getItem("forgotPasswordFlow") === "true" ||
      // Verification flow
      location.pathname === "/verify-otp" ||
      // Hospital selection after verification
      (location.pathname === "/hospitals" && location.state?.fromVerification === true) ||
      // Special signup direct access (for QR render page)
      sessionStorage.getItem("DISABLE_REDIRECTS") === "true";
    
    // Don't interfere with special flows
    if (isSpecialFlow) {
      return;
    }
    
    // Delay navigation slightly to avoid UI flashing
    isNavigatingRef.current = true;
    const timeoutId = setTimeout(() => {
      handleNavigation();
      isNavigatingRef.current = false;
      lastNavigationTimeRef.current = Date.now();
      lastPathRef.current = location.pathname;
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      isNavigatingRef.current = false;
    };
      async function handleNavigation() {      // ---------- CHECK AUTHENTICATION  ----------
      const hasToken = localStorage.getItem("token") !== null;
      const userLoggedIn = localStorage.getItem("userLoggedIn") === "true";
      
      // Check for the intended path from authentication redirect
      const intendedPath = localStorage.getItem("intendedPath") || sessionStorage.getItem("originalIntendedPath");
      
      // Authenticated users
      if (hasToken || userLoggedIn || isAuthenticated) {        // If there's a specialization path stored, prioritize it
        if (intendedPath && intendedPath.includes('/specializations/')) {
          localStorage.removeItem("intendedPath");
          sessionStorage.removeItem("originalIntendedPath");
          navigate(intendedPath, { replace: true });
          return;
        }
        
        // Handle QR flow completion for authenticated users
        const fromQR = localStorage.getItem("fromQR") === "true";
        const fromSpecializationQR = localStorage.getItem("specializationQRFlow") === "active";
        const qrRedirectTarget = sessionStorage.getItem("qrRedirectTarget");
        
        // Handle facility QR redirects
        if (fromQR && qrRedirectTarget) {
          // Clear the QR redirect target to prevent loop and redirect
          sessionStorage.removeItem("qrRedirectTarget");
          navigate(qrRedirectTarget, { replace: true });
          return;
        }          // Handle specialization QR redirects
        if (fromSpecializationQR && qrRedirectTarget) {
          localStorage.removeItem("specializationQRFlow");
          const specializationId = sessionStorage.getItem("qrSpecializationId") || sessionStorage.getItem("pendingSpecialization");
          const facilityId = sessionStorage.getItem("qrFacilityId") || sessionStorage.getItem("pendingFacility");
          
          // Remove the redirect target to prevent loops
          sessionStorage.removeItem("qrRedirectTarget");
          
          navigate("/doctors", {
            state: {
              specializationId,
              facilityId,
              fromQR: true
            },
            replace: true
  });
          return;
        }
          // Redirect authenticated users away from auth pages
        if (location.pathname.startsWith("/auth")) {
          // Check if there's an intended path after authentication
          const intendedPath = localStorage.getItem("intendedPath") || sessionStorage.getItem("originalIntendedPath");
          if (intendedPath && intendedPath !== "/") {
            localStorage.removeItem("intendedPath");
            sessionStorage.removeItem("originalIntendedPath");
            navigate(intendedPath, { replace: true });
          } else {
            navigate("/", { replace: true });
          }
          return;
        }
        
        // Don't redirect authenticated users from get-user immediately after signup
        if (location.pathname === "/get-user" && localStorage.getItem("signupCompleted") !== "true") {
          navigate("/", { replace: true });
          return;
        }
        
        // For authenticated users on allowed pages, do nothing more
        return;
      }
      
      // ---------- HANDLE UNAUTHENTICATED USERS ----------
      
      // PRIMARY SOURCE OF TRUTH: Use isRegistred from auth-store to determine flow
        // For signup page, ensure that unregistered users go through get-user first
      if (location.pathname.startsWith('/auth') && location.search.includes('mode=signup') && !isRegistred) {
        navigate("/get-user", { replace: true });
        return;
      }
      
      // For any non-public route (requires auth)
      if (!isPublicRoute(location.pathname)) {        if (isRegistred) {
          // Registered users who are not authenticated should go to sign in
          navigate("/auth?mode=signin", { replace: true });
        } else {
          // Unregistered users should go to get-user
          navigate("/get-user", { replace: true });
        }
        return;
      }
        // If on get-user and already registered, redirect to signin
      if (location.pathname === "/get-user" && isRegistred) {
        navigate("/auth?mode=signin", { replace: true });
        return;
      }
    }
  }, [location, isAuthenticated, isRegistred, navigate, setSelectedFacility]);

  // Scenario 3: embedded (in an iframe) with no hospital scope → don't run the
  // all-hospitals app inside a partner page; show a "needs a hospital link" notice.
  if (
    isFramed() &&
    !hasActiveFacilityScope() &&
    !isFacilityEntryPath(location.pathname)
  ) {
    return <EmbedNeedsSlug />;
  }

  return <>{children}</>;
};